
import { computed, defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import InputSwitch from 'primevue/inputswitch';
import Textarea from 'primevue/textarea';
import { pickLocalizedText } from '@/utils/localized-text';
import type { ConfigField } from '@/composables/useIntegrations';

interface Option { value: string; label: string }

export default defineComponent({
  name: 'ConfigFieldRenderer',

  components: {
    InputText,
    Password,
    InputNumber,
    Dropdown,
    MultiSelect,
    InputSwitch,
    Textarea,
  },

  props: {
    field: { type: Object as PropType<ConfigField>, required: true },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    modelValue: { type: null as unknown as PropType<any>, default: undefined },
    /** Message d'erreur de validation pour ce champ (vide = aucune erreur). */
    fieldError: { type: String, default: '' },
    /** État remote (uniquement pertinent si `field.remote`). */
    remoteLoading: { type: Boolean, default: false },
    remoteError: { type: Boolean, default: false },
    remoteOptions: { type: Array as PropType<Option[]>, default: () => [] },
  },

  emits: ['update:modelValue', 'remote-retry'],

  setup(props) {
    const { t, locale } = useI18n();

    const localize = (
      value: string | Record<string, string> | undefined,
    ): string => pickLocalizedText(value as any, locale.value);

    const label = computed(() => localize(props.field.label) || props.field.key);
    const placeholder = computed(() => localize(props.field.placeholder));
    const helpText = computed(() => localize(props.field.helpText));

    // Options remote (si champ remote) sinon options statiques du schéma.
    const resolvedOptions = computed<Option[]>(() => {
      if (props.field.remote) return props.remoteOptions || [];
      return (props.field.options || []).map((o) => ({
        value: o.value,
        label: localize(o.label) || o.value,
      }));
    });

    return {
      t,
      label,
      placeholder,
      helpText,
      resolvedOptions,
    };
  },
});
